import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Phone, Mail, MessageSquare, Check, CalendarClock, Clock } from "lucide-react";
import api from "../../lib/api";
import { PageHeader, Card, Badge, Empty, RedButton, GhostButton } from "../../components/crm/ui";
import { toast } from "sonner";

export default function FollowUps() {
  const [params] = useSearchParams();
  const [scope, setScope] = useState(params.get("scope") || "");
  const [tasks, setTasks] = useState([]);

  const load = useCallback(() => {
    api.get("/tasks", { params: { scope, status: scope ? "" : "Pending" } }).then(({ data }) => setTasks(data.items)).catch(() => {});
  }, [scope]);
  useEffect(() => { load(); }, [load]);

  const complete = async (id) => { await api.post(`/tasks/${id}/complete`, {}); toast.success("Completed"); load(); };
  const reschedule = async (id) => {
    const d = prompt("Reschedule to date (YYYY-MM-DD):");
    if (!d) return;
    await api.post(`/tasks/${id}/reschedule`, { due_date: new Date(d).toISOString() });
    toast.success("Rescheduled"); load();
  };

  return (
    <div>
      <PageHeader title="Follow-Ups" subtitle="Make sure somebody follows up — nothing slips through." />
      <div className="flex gap-2 mb-4">
        {[["", "All Pending"], ["overdue", "Overdue"]].map(([v, l]) => (
          <button key={v} onClick={() => setScope(v)} data-testid={`fu-tab-${v || "all"}`} className={`px-4 py-2 rounded-md text-sm font-semibold ${scope === v ? "bg-alto-black text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{l}</button>
        ))}
      </div>

      {tasks.length === 0 ? (
        <Card className="p-6"><Empty icon={CalendarClock} title="No follow-ups here" hint="Create follow-up tasks from a contact's profile." /></Card>
      ) : (
        <Card className="divide-y divide-slate-100">
          {tasks.map((t) => (
            <div key={t.id} className="p-4 flex items-center justify-between gap-3" data-testid={`followup-${t.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Link to={`/app/contacts/${t.contact?.id}`} className="font-semibold text-slate-900 hover:text-alto-red">{t.contact?.name || "Unknown"}</Link>
                  {t.status_display === "Overdue" && <Badge tone="red">Overdue</Badge>}
                  <Badge tone={t.priority === "Urgent" || t.priority === "High" ? "amber" : "slate"}>{t.priority}</Badge>
                </div>
                <p className="text-xs text-slate-500 mt-1 flex items-center gap-1.5"><Clock size={11} /> {t.task_type} · {t.description} · due {t.due_date?.slice(0,10)} · {t.contact?.latest_source || "—"}</p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {t.contact?.phone && <a href={`tel:${t.contact.phone}`} className="p-2 rounded-md hover:bg-slate-100 text-slate-500"><Phone size={15} /></a>}
                {t.contact?.phone && <a href={`sms:${t.contact.phone}`} className="p-2 rounded-md hover:bg-slate-100 text-slate-500"><MessageSquare size={15} /></a>}
                {t.contact?.email && <a href={`mailto:${t.contact.email}`} className="p-2 rounded-md hover:bg-slate-100 text-slate-500"><Mail size={15} /></a>}
                <GhostButton className="!py-1.5 !px-2.5" onClick={() => reschedule(t.id)} data-testid={`reschedule-${t.id}`}>Reschedule</GhostButton>
                <RedButton className="!py-1.5 !px-3" onClick={() => complete(t.id)} data-testid={`complete-fu-${t.id}`}><Check size={15} /></RedButton>
              </div>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
